import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Eye, EditIcon, CheckCircle, XCircle, Clock, ShieldAlert, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TableRow, TableCell } from '@/components/ui/table';
import { Report } from '@/types/report';
import { ReportStatusBadge } from './ReportStatusBadge';
import { DeductHoursButton } from '../admin/DeductHoursButton';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';

interface ReportTableRowProps {
  report: Report;
  formatDate: (dateString: string) => string;
  formatTime: (dateString: string) => string;
  showTeacherDisplay: boolean;
  showAdminColumns: boolean;
  onReportUpdate?: () => void;
}

export const ReportTableRow: React.FC<ReportTableRowProps> = ({ 
  report, 
  formatDate, 
  formatTime, 
  showTeacherDisplay, 
  showAdminColumns, 
  onReportUpdate 
}) => {
  const { t } = useTranslation();
  return (
    <TableRow key={report.id}>
      <TableCell>{formatDate(report.start_date || report.teaching_date)}</TableCell>
      <TableCell>
        {report.start_date && report.end_date ? (
          <>
            {formatTime(report.start_date)} - {formatTime(report.end_date)}
          </>
        ) : (
          report.teaching_time_slot
        )}
      </TableCell>
      
      <TableCell>
        {report.student_nickname}{' '}
        <span className="text-gray-500 text-xs">({report.student_full_name})</span>
      </TableCell>
      
      {showTeacherDisplay && (
        <TableCell>
          <div>
            <div className="text-sm">{report.teacher_display || '-'}</div>
            {report.submitter_email && (
              <div className="text-xs text-gray-500">
                {report.submitter_email}
                {report.created_by_admin && (
                  <span className="ml-1 inline-flex items-center">
                    <ShieldAlert className="h-3 w-3 text-amber-500 mr-1" />
                    <span className="text-amber-500">Admin</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </TableCell>
      )}
      
      <TableCell>
        <div className="flex flex-col gap-1">
          <ReportStatusBadge 
            status={report.status} 
            rejectionReason={report.rejection_reason} 
          />
          {report.other_subject && (
            <Badge variant="outline" className="w-fit flex items-center gap-1 border-amber-400 text-amber-600">
              <Tag className="h-3 w-3" />
              <span className="text-xs">{t('reports.table.otherTopic')}</span>
            </Badge>
          )}
        </div>
      </TableCell>
      
      {showAdminColumns && (
        <TableCell>
          <span className="text-sm">{report.learning_status || '-'}</span>
        </TableCell>
      )}
      
      {/* Add teaching content column for admin */}
      {showAdminColumns && (
        <TableCell className="max-w-xs truncate">
          <span className="text-sm" title={report.teaching_content || '-'}>
            {report.teaching_content || '-'}
          </span>
        </TableCell>
      )}
      
      {showAdminColumns && (
        <TableCell className="text-center">
          {report.submitted_on_time === true ? (
            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
          ) : report.submitted_on_time === false ? (
            <XCircle className="h-5 w-5 text-red-500 mx-auto" />
          ) : (
            '-'
          )}
        </TableCell>
      )}
      
      {showAdminColumns && (
        <TableCell className="text-center">
          {report.hours_deducted ? (
            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
          ) : (
            <Clock className="h-5 w-5 text-amber-500 mx-auto" />
          )}
        </TableCell>
      )}
      
      <TableCell>
        {format(new Date(report.created_at), 'dd/MM/yyyy HH:mm')}
      </TableCell>
      
      <TableCell className="text-right space-x-2">
        <Button variant="ghost" size="sm" asChild className="hover:text-blue-600">
          <Link to={`/reports/${report.id}`}>
            <Eye className="h-4 w-4 mr-1" />
            {t('reports.table.view')}
          </Link>
        </Button>
        
        {showAdminColumns && (
          <>
            <Button variant="ghost" size="sm" asChild className="hover:text-blue-600">
              <Link to={`/reports/${report.id}/edit`}>
                <EditIcon className="h-4 w-4 mr-1" />
                แก้ไข
              </Link>
            </Button>
            {!report.hours_deducted && (
              <DeductHoursButton reportId={report.id} onSuccess={onReportUpdate} />
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
};
